import { useEffect, useRef } from 'react';

interface Particle {
  x: number;
  y: number;
  targetX: number;
  targetY: number;
  startX: number;
  startY: number;
  color: string;
}

export function ThankYouAnimation() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationFrameRef = useRef<number>();

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Retry mechanism: if canvas has no dimensions yet (not laid out), wait and retry
    const initAnimation = () => {
      const rect = canvas.getBoundingClientRect();
      if (rect.width === 0 || rect.height === 0) {
        // Canvas not visible yet — retry on next frame
        animationFrameRef.current = requestAnimationFrame(initAnimation);
        return;
      }

      // Set canvas size
      const updateSize = () => {
        const dpr = window.devicePixelRatio || 1;
        const rect = canvas.getBoundingClientRect();
        canvas.width = rect.width * dpr;
        canvas.height = rect.height * dpr;
        ctx.scale(dpr, dpr);
      };
      updateSize();
      window.addEventListener('resize', updateSize);

      // Configuration
      const WIDTH = canvas.getBoundingClientRect().width;
      const HEIGHT = canvas.getBoundingClientRect().height;
      const TEXT = 'Enjoy your Art';
      const FONT_SIZE = Math.min(WIDTH / 8, 120); // Larger for fullscreen
      const N_DOTS = 900;
      const DOT_RADIUS = 3;
      const ANIMATION_DURATION = 2500; // ms
      
      // Colorful dots (matching Python code)
      const DOT_COLORS = [
        '#FFFFFF', // white
        '#FF6347', // tomato/red-orange
        '#87CEEB', // light sky blue
        '#90EE90', // light green
        '#FFD700', // gold
        '#BA55D3', // medium orchid/purple
      ];

      // Create text mask to find target positions
      const createTextMask = (): number[][] => {
        const tempCanvas = document.createElement('canvas');
        tempCanvas.width = Math.max(1, Math.ceil(WIDTH));
        tempCanvas.height = Math.max(1, Math.ceil(HEIGHT));
        const tempCtx = tempCanvas.getContext('2d');
        if (!tempCtx) return [];

        tempCtx.fillStyle = 'white';
        tempCtx.font = `bold ${FONT_SIZE}px 'Playfair Display', Georgia, serif`;
        tempCtx.textAlign = 'center';
        tempCtx.textBaseline = 'middle';
        tempCtx.fillText(TEXT, WIDTH / 2, HEIGHT / 2);

        const imageData = tempCtx.getImageData(0, 0, Math.ceil(WIDTH), Math.ceil(HEIGHT));
        const pixels: number[][] = [];

        // Sample every 2 pixels for performance
        const w = Math.ceil(WIDTH);
        for (let y = 0; y < Math.ceil(HEIGHT); y += 2) {
          for (let x = 0; x < w; x += 2) {
            const i = (y * w + x) * 4;
            if (imageData.data[i + 3] > 128) {
              pixels.push([x, y]);
            }
          }
        }

        return pixels;
      };

      const textPixels = createTextMask();
      
      if (textPixels.length === 0) {
        console.warn('No pixels found for text — font may not be loaded yet, retrying...');
        // Font might not be loaded yet — retry after a short delay
        const retryTimer = setTimeout(() => {
          if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
          initAnimation();
        }, 300);
        window.removeEventListener('resize', updateSize);
        return () => {
          clearTimeout(retryTimer);
          window.removeEventListener('resize', updateSize);
        };
      }

      // Create particles
      const particles: Particle[] = [];
      const numParticles = Math.min(N_DOTS, textPixels.length);
      
      // Randomly select target positions
      const selectedIndices = new Set<number>();
      while (selectedIndices.size < numParticles) {
        selectedIndices.add(Math.floor(Math.random() * textPixels.length));
      }

      const selectedPixels = Array.from(selectedIndices).map(i => textPixels[i]);

      for (const [targetX, targetY] of selectedPixels) {
        const startX = Math.random() * WIDTH;
        const startY = Math.random() * HEIGHT;
        const color = DOT_COLORS[Math.floor(Math.random() * DOT_COLORS.length)];
        
        particles.push({
          x: startX,
          y: startY,
          startX,
          startY,
          targetX,
          targetY,
          color, // Add color to each particle
        });
      }

      // Easing function (ease-out cubic)
      const easeOutCubic = (t: number): number => {
        return 1 - Math.pow(1 - t, 3);
      };

      let startTime: number | null = null;
      let isComplete = false;

      const animate = (timestamp: number) => {
        if (!startTime) startTime = timestamp;
        const elapsed = timestamp - startTime;

        // Clear canvas
        ctx.fillStyle = 'black';
        ctx.fillRect(0, 0, WIDTH, HEIGHT);

        if (elapsed < ANIMATION_DURATION) {
          // Animating
          const progress = elapsed / ANIMATION_DURATION;
          const easedProgress = easeOutCubic(progress);

          // Update and draw particles with subtle glow
          for (const particle of particles) {
            particle.x = particle.startX + (particle.targetX - particle.startX) * easedProgress;
            particle.y = particle.startY + (particle.targetY - particle.startY) * easedProgress;

            // Subtle glow effect with particle's color
            ctx.shadowBlur = 4;
            ctx.shadowColor = particle.color;
            ctx.fillStyle = particle.color;
            
            ctx.beginPath();
            ctx.arc(particle.x, particle.y, DOT_RADIUS, 0, Math.PI * 2);
            ctx.fill();
          }
          
          // Reset shadow
          ctx.shadowBlur = 0;

          animationFrameRef.current = requestAnimationFrame(animate);
        } else if (!isComplete) {
          // Animation complete - draw final position and stop
          isComplete = true;
          
          // Draw final position with glow
          for (const particle of particles) {
            particle.x = particle.targetX;
            particle.y = particle.targetY;

            ctx.shadowBlur = 4;
            ctx.shadowColor = particle.color;
            ctx.fillStyle = particle.color;
            
            ctx.beginPath();
            ctx.arc(particle.x, particle.y, DOT_RADIUS, 0, Math.PI * 2);
            ctx.fill();
          }
          
          ctx.shadowBlur = 0;
          // Animation stops here - no more frames requested
        }
      };

      animationFrameRef.current = requestAnimationFrame(animate);

      return () => {
        window.removeEventListener('resize', updateSize);
        if (animationFrameRef.current) {
          cancelAnimationFrame(animationFrameRef.current);
        }
      };
    }; // end initAnimation

    initAnimation();

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="w-full h-full"
      style={{ backgroundColor: 'black' }}
    />
  );
}